import { useEffect, useState } from "react";
import {
  Applicant,
  CompanyShareholder,
  IndividualShareholder,
  useLoanApplication,
} from "../../../../contexts";
import { Button, Divider } from "../../../../components";
import { PlusIcon } from "../../../../assets/icons";
import ShareholderInfo from "../ShareholderInfo";
import ShareholderModal from "../../OwnersInformation/ShareholderModal";
import { ShareholderFields } from "../../OwnersInformation/service";
import {
  useDeleteCompanyShareholder,
  useDeleteIndividualShareholder,
} from "../../../../hooks";
import DeleteConfirmationModal from "../DeleteConfirmationModal";
import {
  CompanyShareholderType,
  ShareholderEntityType,
} from "../../../../hooks/api/types";
import ApplicantInformationModal from "../../OwnersInformation/ApplicantInformationModal";
import { SumSubKycModal } from "../../OwnersInformation";
import ShareHolderDialogModal from "../ShareHolderDialogModal";

const ORDINALS = [
  "",
  "First",
  "Second",
  "Third",
  "Fourth",
  "Fifth",
  "Sixth",
  "Seventh",
  "Eighth",
  "Ninth",
  "Tenth",
];

const getShareholderOrdinal = (position: number) => {
  return ORDINALS[position + 2] ?? "";
};

const OWNER_INFO_TOOLTIP =
  "You can add Emirates ID of shareholder at this step also by clicking the edit icon.";

const OwnersInformationReviewSection = () => {
  const [shareholderModalOpen, setShareholderModalOpen] = useState(false);
  const [applicantInformationModalOpen, setApplicantInformationModalOpen] =
    useState(false);

  const [kycModalOpen, setKycModalOpen] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [shareHolderMessageModal, setShareHolderMessageModal] = useState(false);
  const [currentShareholder, setCurrentShareholder] = useState<
    IndividualShareholder | CompanyShareholder | null
  >(null);

  const {
    state: { applicant, individualShareholders, companyShareholders },
    actions: { update, refetch },
  } = useLoanApplication();
  const { mutateAsync: deleteIndividualShareholder } =
    useDeleteIndividualShareholder();
  const { mutateAsync: deleteCompanyShareholder } =
    useDeleteCompanyShareholder();

  useEffect(() => {
    if (
      !individualShareholders?.some((s) => s.hasPOA) &&
      !companyShareholders.some((s) => s.hasPOA)
    ) {
      setShareHolderMessageModal(true);
    }
  }, [individualShareholders, companyShareholders]);

  const handleDeleteIndividualShareholder = async (id: string) => {
    await deleteIndividualShareholder(id);
    // Locally update the state after deleting from the API
    await update(
      {
        individualShareholders: individualShareholders.filter(
          (shareholder) => shareholder.id !== id
        ),
      },
      { local: true }
    );
  };

  const handleDeleteCompanyShareholder = async (id: string) => {
    await deleteCompanyShareholder(id);
    // Locally update the state after deleting from the API
    await update(
      {
        companyShareholders: companyShareholders.filter(
          (shareholder) => shareholder.id !== id
        ),
      },
      { local: true }
    );
  };

  const handleApplicantUpdate = async (values: Applicant) => {
    if (
      ((applicant.firstName !== values.firstName ||
        applicant.lastName !== values.lastName) &&
        (applicant.isStakeholder || applicant.hasPOA)) ||
      (!applicant.isStakeholder && values.isStakeholder) ||
      (!applicant.hasPOA && values.hasPOA)
    ) {
      await refetch();
      setKycModalOpen(true);
    }
    setApplicantInformationModalOpen(false);
  };

  return (
    <div className="cx-bg-white cx-rounded-lg cx-w-full cx-shadow-md cx-p-5 md:cx-p-10 cx-min-w-[343px] cx-mt-10">
      <div className="cx-flex cx-flex-row  cx-place-content-between max-md:cx-justify-between max-md:cx-items-center cx-w-full">
        <div className="cx-flex cx-text-2xl cx-font-bold">
          Shareholders information
        </div>
        <Button
          label={
            <div className="cx-flex cx-items-center max-md:cx-min-w-[130px] max-md:cx-text-s max-md:cx-max-h-[37px] max-md:cx-max-w-[162px]">
              <PlusIcon className="cx-mr-2 max-md:cx-mt-1" />
              <div>Add shareholder</div>
            </div>
          }
          slim={true}
          onClick={() => {
            setCurrentShareholder(null);
            setShareholderModalOpen(true);
          }}
        />
      </div>
      <div className="cx-w-full cx-grid cx-grid-cols-1 md:cx-grid-cols-2 lg:cx-grid-cols-3 xl:cx-grid-cols-4 cx-gap-x-4">
        {applicant && (
          <div className="cx-col-span-1">
            <ShareholderInfo
              title="Your information"
              tooltipText={OWNER_INFO_TOOLTIP}
              name={`${applicant.firstName} ${applicant.lastName}`}
              email={applicant.email}
              phoneNumber={`${applicant.countryCode}${applicant.localNumber}`}
              onEdit={() => {
                setApplicantInformationModalOpen(true);
              }}
            />
          </div>
        )}
        {(() => {
          const filteredIndividuals = individualShareholders.filter(
            (s) => s.type === CompanyShareholderType.SHAREHOLDER
          );
          return (
            <>
              {filteredIndividuals.map((shareholder, idx) => (
                <div key={shareholder.email} className="cx-col-span-1">
                  <ShareholderInfo
                    title={`${getShareholderOrdinal(idx)} shareholder`}
                    tooltipText={OWNER_INFO_TOOLTIP}
                    name={`${shareholder.firstName} ${shareholder.lastName}`}
                    email={shareholder.email}
                    phoneNumber={`${shareholder.countryCode}${shareholder.localNumber}`}
                    onEdit={() => {
                      setCurrentShareholder(shareholder);
                      setShareholderModalOpen(true);
                    }}
                    onDelete={() => {
                      setCurrentShareholder(shareholder);
                      setConfirmationModal(true);
                    }}
                  />
                  {filteredIndividuals.length &&
                    idx + 1 < filteredIndividuals.length}
                </div>
              ))}
              {companyShareholders.map((shareholder, idx) => (
                <div key={shareholder.email} className="cx-col-span-1">
                  <ShareholderInfo
                    title={`${getShareholderOrdinal(
                      filteredIndividuals.length + idx
                    )} shareholder`}
                    tooltipText={OWNER_INFO_TOOLTIP}
                    name={shareholder.name}
                    email={shareholder.email}
                    phoneNumber={`${shareholder.countryCode}${shareholder.localNumber}`}
                    onEdit={() => {
                      setCurrentShareholder(shareholder);
                      setShareholderModalOpen(true);
                    }}
                    onDelete={() => {
                      setCurrentShareholder(shareholder);
                      setConfirmationModal(true);
                    }}
                  />
                  {companyShareholders.length &&
                    idx + 1 < companyShareholders.length}
                </div>
              ))}
            </>
          );
        })()}
      </div>

      <ShareholderModal
        isOpen={shareholderModalOpen}
        onClose={() => setShareholderModalOpen(false)}
        onCancel={() => setShareholderModalOpen(false)}
        onSubmit={() => setShareholderModalOpen(false)}
        shareholder={currentShareholder as ShareholderFields}
      />
      <ApplicantInformationModal
        isOpen={applicantInformationModalOpen}
        onClose={() => setApplicantInformationModalOpen(false)}
        onCancel={() => setApplicantInformationModalOpen(false)}
        onSubmit={handleApplicantUpdate}
      />
      <SumSubKycModal
        isOpen={kycModalOpen}
        onPrev={() => {
          setKycModalOpen(false);
          setApplicantInformationModalOpen(true);
        }}
        onClose={() => setKycModalOpen(false)}
        onSubmit={() => setKycModalOpen(false)}
      />

      <DeleteConfirmationModal
        isOpen={confirmationModal}
        onClose={() => setConfirmationModal(false)}
        onSubmit={() => {
          if (!currentShareholder) return;

          if (
            currentShareholder?.__entityType ===
            ShareholderEntityType.INDIVIDUAL
          ) {
            handleDeleteIndividualShareholder(currentShareholder.id!);
          } else {
            handleDeleteCompanyShareholder(currentShareholder.id!);
          }
          setConfirmationModal(false);
        }}
      />
      <ShareHolderDialogModal
        isOpen={shareHolderMessageModal}
        onClose={() => setShareHolderMessageModal(false)}
      />
    </div>
  );
};

export default OwnersInformationReviewSection;
